/**
 * IP制限ユーティリティ関数群
 * IPv6基準で管理し、個別IPアドレスの完全一致のみをサポート
 * ページ・要素中心の制限設定に対応
 */

import ipaddr from "ipaddr.js"
import type { IPRestrictionConfig } from "../../../admin/config/ipRestrictionConfig"
import { ipRestrictionConfig } from "../../../admin/config/ipRestrictionConfig"

/**
 * アドレスからポート番号を除去（IPv4とIPv6の両方に対応）
 * CloudFrontの仕様により、cloudfront-viewer-addressは常にポート番号を含む。また、IPv6アドレスは常に角括弧で囲まれている。
 * 例:
 * removePortFromAddress("192.168.1.1:8080") → "192.168.1.1"
 * removePortFromAddress("[2400:4051:4e83:4c00:b0e2:1e75:61d1:1173]:65358") → "2400:4051:4e83:4c00:b0e2:1e75:61d1:1173"
 * removePortFromAddress("[2001:db8::a:1]:443") → "2001:db8::a:1"
 * @param address - ポート番号を含むアドレス（CloudFront仕様）
 * @returns ポート番号が除去されたアドレス
 */
const removePortFromAddress = (address: string): string => {
  if (!address || typeof address !== "string") {
    return address
  }
  const trimmedAddress = address.trim()

  // CloudFront cloudfront-viewer-address のipv6準拠
  // 角括弧で囲まれたIPv6アドレスの場合 (例: [2001:db8::1]:8080) CloudFrontの仕様により、は常にポート番号を含む
  if (trimmedAddress.startsWith("[") && trimmedAddress.includes("]")) {
    const ipv6_in_brackets = trimmedAddress.split("]")[0].split("[")[1]
    if (ipaddr.isValid(ipv6_in_brackets)) {
      return ipv6_in_brackets
    }
  }

  // ipv4 or ipv6([]がない場合はポート番号なし)
  if (ipaddr.isValid(trimmedAddress)) {
    // すでにポート番号がない場合はそのまま返す
    return trimmedAddress
  }

  // ipv4 ポート番号付き
  const lastColonIndex = trimmedAddress.lastIndexOf(":")
  const ipPart = trimmedAddress.substring(0, lastColonIndex)
  // ipaddr.jsで有効なIPアドレスかチェックしてから返す
  if (ipaddr.isValid(ipPart)) {
    return ipPart
  }

  // 有効なIPアドレスでない場合は元のアドレスを返す
  return trimmedAddress
}

/**
 * IPv4,6をIPv6基準に正規化（IPv6アドレスはそのまま保持）
 * 例:
 * normalizeToIPv6("192.168.1.100") → "::ffff:192.168.1.100"
 * normalizeToIPv6("::1") → "::1" (変更なし)
 * @param ip - 正規化するIPアドレス
 * @returns IPv6形式のIPアドレス
 */
const normalizeToIPv6 = (ip: string): string => {
  if (!ip || typeof ip !== "string") {
    return ip
  }

  const trimmedIP = ip.trim()

  try {
    const parsedIP = ipaddr.parse(trimmedIP)

    if (parsedIP.kind() === "ipv4") {
      // IPv4アドレスの場合、IPv4射影アドレスに変換
      return `::ffff:${trimmedIP}`
    } else if (parsedIP.kind() === "ipv6") {
      // IPv6アドレスの場合、そのまま返す
      return trimmedIP
    }
  } catch {
    // ipaddr.jsで解析できない場合は、無効なIPアドレスとして扱う
    return trimmedIP
  }

  // 変換できない場合は元のIPアドレスを返す
  return trimmedIP
}

/**
 * IPアドレスが許可リストに含まれているかチェック（完全一致 + CIDR形式対応）
 * 例:
 * isIPAllowed("::ffff:127.0.0.1", ["::ffff:127.0.0.1", "::ffff:192.168.1.100"]) → true
 * isIPAllowed("::ffff:127.0.0.1", ["::ffff:192.168.1.100"]) → false
 * isIPAllowed("::ffff:192.168.1.50", ["192.168.1.0/24"]) → true (CIDR形式)
 * isIPAllowed("::ffff:10.0.0.5", ["10.0.0.0/8"]) → true (CIDR形式)
 * @param ip - チェックするIPアドレス
 * @param allowedIPs - 許可されたIPアドレスの配列（CIDR形式も対応）
 * @returns 許可されている場合true
 */
const isIPAllowed = (ip: string, allowedIPs: string[]): boolean => {
  // クライアントIPをIPv6基準に正規化
  const normalizedClientIP = normalizeToIPv6(ip)

  let clientIPAddr: any
  try {
    clientIPAddr = ipaddr.parse(normalizedClientIP)
  } catch {
    console.warn(
      `Invalid IP address: ${ip} (normalized: ${normalizedClientIP})`
    )
    return false
  }

  if (!allowedIPs || !Array.isArray(allowedIPs)) {
    console.warn("Invalid allowedIPs parameter")
    return false
  }

  return allowedIPs.some((allowedIP) => {
    if (!allowedIP || typeof allowedIP !== "string") {
      return false
    }

    const trimmedAllowedIP = allowedIP.trim()

    // CIDR形式のチェック
    if (trimmedAllowedIP.includes("/")) {
      try {
        const [networkStr, prefixLengthStr] = trimmedAllowedIP.split("/")
        const prefixLength = parseInt(prefixLengthStr, 10)

        if (isNaN(prefixLength)) {
          console.warn(`Invalid prefix length in CIDR: ${trimmedAllowedIP}`)
          return false
        }

        // ネットワークアドレスを解析
        let networkAddr: any
        const networkIP = networkStr.trim()
        try {
          networkAddr = ipaddr.parse(networkIP)
        } catch {
          console.warn(`Invalid network IP in CIDR: ${networkIP}`)
          return false
        }

        // IPv4射影アドレスのCIDR判定（::ffff:192.168.1.0/24 のような形式）
        if (
          networkIP.startsWith("::ffff:") &&
          normalizedClientIP.startsWith("::ffff:")
        ) {
          const networkIPv4 = networkIP.substring(7) // "::ffff:" を除去
          const clientIPv4 = normalizedClientIP.substring(7) // "::ffff:" を除去
          try {
            const networkIPv4Addr = ipaddr.parse(networkIPv4)
            const clientIPv4Addr = ipaddr.parse(clientIPv4)
            return clientIPv4Addr.match(networkIPv4Addr, prefixLength) // CIDRを考慮した判定
          } catch {
            return false
          }
        }

        return clientIPAddr.match(networkAddr, prefixLength) // CIDRを考慮した判定
      } catch (error) {
        console.warn(`Invalid CIDR format: ${trimmedAllowedIP}`, error)
        return false
      }
    }

    // 完全一致チェック（登録時は全てIPv6形式のため、単純な比較で十分）
    const normalizedAllowedIP = normalizeToIPv6(trimmedAllowedIP)
    return normalizedAllowedIP === normalizedClientIP
  })
}

/**
 * Next.jsリクエストからクライアントIPアドレスを取得（IPv6基準）
 * プロキシサーバー経由でも正確にIPを取得
 * @param request - Next.jsのリクエストオブジェクト
 * @returns クライアントIPアドレス（IPv6形式）、取得できない場合は'unknown'
 */
export const getClientIP = (request: any): string => {
  if (!request) {
    return "unknown"
  }
  console.log("request.headers", request.headers)

  // CloudFront-Viewer-Address ヘッダーを最優先（AWS CloudFront使用時）
  const cloudFrontViewerAddress =
    request.headers?.["cloudfront-viewer-address"] ||
    request.headers?.["CloudFront-Viewer-Address"] ||
    request.headers?.get?.("cloudfront-viewer-address") ||
    request.headers?.get?.("CloudFront-Viewer-Address")
  if (cloudFrontViewerAddress) {
    const addressPart = removePortFromAddress(
      cloudFrontViewerAddress.toString()
    )
    if (addressPart && addressPart !== "unknown") {
      return normalizeToIPv6(addressPart)
    }
  }

  // X-Forwarded-For ヘッダーを最優先（プロキシ経由時）(ポートは付与されない
  const xForwardedFor =
    request.headers?.["x-forwarded-for"] ||
    request.headers?.["X-Forwarded-For"] ||
    request.headers?.get?.("x-forwarded-for") ||
    request.headers?.get?.("X-Forwarded-For")
  if (xForwardedFor) {
    // 複数のプロキシを経由している場合、最初のIPが実際のクライアントIP
    const firstIP = xForwardedFor.toString().split(",")[0].trim()
    if (firstIP && firstIP !== "unknown") {
      return normalizeToIPv6(firstIP)
    }
  }

  // remoteAddress（直接接続時）
  const remoteAddress =
    request.connection?.remoteAddress ||
    request.socket?.remoteAddress ||
    request.ip
  if (remoteAddress) {
    const ip = remoteAddress.toString().trim()
    return normalizeToIPv6(ip)
  }

  // 取得できない場合はunknownを返す（安全側に倒す）
  return "unknown"
}

/**
 * 設定に基づくページ別アクセス制限チェック（IPv6基準）
 * @param clientIP - クライアントIPアドレス
 * @param pageName - ページ名
 * @param pagesConfig - ページ設定オブジェクト
 * @returns アクセスが許可されている場合true
 */
export const checkPageAccess = (
  clientIP: string,
  pageName: string,
  pagesConfig: IPRestrictionConfig["pages"]
): boolean => {
  // IPv6基準に正規化
  const normalizedIP = normalizeToIPv6(clientIP)

  const pageSettings = findMatchingPageConfig(pageName, pagesConfig)

  // 設定にないページは全て許可
  if (!pageSettings) {
    return true
  }

  // allowed_ipsが空配列の場合は全て許可
  if (!pageSettings.allowed_ips || pageSettings.allowed_ips.length === 0) {
    return true
  }

  // IP制限チェック
  return isIPAllowed(normalizedIP, pageSettings.allowed_ips)
}

/**
 * 要素アクセス許可をチェック（IPv6基準）
 * @param clientIP - クライアントIPアドレス
 * @param pageName - ページ名
 * @param elementName - 要素名
 * @returns アクセスが許可されている場合true
 */
export const checkElementAccess = (
  clientIP: string,
  pageName: string,
  elementName: string
): boolean => {
  // IPv6基準に正規化
  const normalizedIP = normalizeToIPv6(clientIP)

  const pageSettings = findMatchingPageConfig(
    pageName,
    ipRestrictionConfig.pages
  )

  // ページ設定がない場合はデフォルト許可（全IPアクセス可能）
  if (!pageSettings) {
    return true
  }

  const elementSettings = pageSettings[elementName]

  // 要素設定がない場合はデフォルト許可
  if (!elementSettings || typeof elementSettings !== "object") {
    return true
  }

  // 要素レベルのallowed_ipsが設定されている場合
  if (
    "allowed_ips" in elementSettings &&
    Array.isArray(elementSettings.allowed_ips)
  ) {
    // allowed_ipsが空配列の場合は全て許可（ページレベルと同じ挙動）
    if (elementSettings.allowed_ips.length === 0) {
      return true
    }
    return isIPAllowed(normalizedIP, elementSettings.allowed_ips)
  }

  // 要素レベルの制限がない場合はデフォルト許可
  return true
}

/**
 * ワイルドカードパターン（*）に対応したページ設定の検索
 * @param path - 検索するページパス
 * @param settings - 設定オブジェクト
 * @returns マッチするページ設定、見つからない場合はundefined
 */
function findMatchingPageConfig(
  path: string,
  settings: IPRestrictionConfig["pages"]
): { allowed_ips?: string[]; [key: string]: any } | undefined {
  // ワイルドカードパターン（*）に対応
  for (const page in settings) {
    if (page.endsWith("/*")) {
      const prefix = page.slice(0, -2)
      if (path.startsWith(prefix + "/") || path === prefix) {
        return settings[page]
      }
    }
  }

  // ワイルドカードに該当しない場合は完全一致を確認
  return settings[path]
}

export { isIPAllowed, normalizeToIPv6, removePortFromAddress } // test.tsに渡す用
